import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import StudioRowActions from "../../common/Components/GeoList/StudioRowActions";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  createWorkerCategory,
  listWorkerCategoryPosts,
  seedWorkerCategories,
  updateWorkerCategory,
  updateWorkerCodePolicy,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import WorkersNav from "./workers/WorkersNav";
import { HkpPost, postLabel, previewCode, useWorkerSetup, WorkerCategory } from "./workers/useWorkerSetup";
import "./workers/karkhana-workers.scss";
import PageHeader, { HeaderButton } from "./ui/PageHeader";
import { useKT } from "./i18n/kt";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const PAY_LABEL: Record<string, string> = { PIECE: "Per piece", TIME: "By time", JOB: "Per job" };

// userRole "" = Karkhana only (no HKP user).
const emptyForm = () => ({
  prefix: "",
  name: "",
  defaultPayBasis: "TIME",
  canWeave: false,
  canHelp: false,
  nameHints: "",
  userRole: "",
});

const TEMPLATES = [
  {
    key: "BANARASI" as const,
    title: "Banarasi karkhana",
    body: "K Karigar, J Jodiya, D Dyer, O Office, N Nari, S Safai, JAL Jali, Driver, L LPG, DN Designer, B Barber, plus F, T, M, R, DH, TW to rename.",
  },
  {
    key: "GENERIC" as const,
    title: "Simple start",
    body: "W Weaver, H Helper, D Dyer, O Other. Rename them or add your own any time.",
  },
];

const KarkhanaWorkerCategoriesPage = () => {
  const history = useHistory();
  const role = readRole();
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const { categories, policy, reviewCount, loaded, reload } = useWorkerSetup();
  const [seeding, setSeeding] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WorkerCategory | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState("");
  const [numbering, setNumbering] = useState("ORG_WIDE");
  const [posts, setPosts] = useState<HkpPost[]>([]);
  const { t } = useKT();

  useEffect(() => {
    if (!canManageKarkhana(role)) history.replace(kp.home());
  }, [role, history]);

  useEffect(() => {
    if (!canManageKarkhana(role)) return;
    listWorkerCategoryPosts()
      .then((res: any) => setPosts(res?.success && Array.isArray(res.data) ? res.data : []))
      .catch(() => setPosts([]));
  }, [role]);

  useEffect(() => {
    if (policy) {
      setFormat(policy.format);
      setNumbering(policy.numbering);
    }
  }, [policy]);

  const seed = async (template: "BANARASI" | "GENERIC") => {
    setSeeding(template);
    try {
      const res: any = await seedWorkerCategories(template);
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not add categories", type: "danger" });
        return;
      }
      await reload();
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setSeeding("");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (c: WorkerCategory) => {
    setEditing(c);
    setForm({
      prefix: c.prefix,
      name: c.name,
      defaultPayBasis: c.defaultPayBasis,
      canWeave: c.canWeave,
      canHelp: c.canHelp,
      nameHints: (c.nameHints || []).join(", "),
      userRole: c.userRole || "",
    });
    setOpen(true);
  };

  const persist = async () => {
    if (!/^[A-Za-z]{1,10}$/.test(form.prefix.trim())) {
      notifications({ title: "Error", message: "A prefix is 1 to 10 letters, e.g. K", type: "danger" });
      return;
    }
    setSaving(true);
    try {
      const body = {
        prefix: form.prefix.trim().toUpperCase(),
        name: form.name.trim(),
        defaultPayBasis: form.defaultPayBasis,
        canWeave: form.canWeave,
        canHelp: form.canHelp,
        nameHints: form.nameHints.split(",").map((h) => h.trim()).filter(Boolean),
        userRole: form.userRole,
      };
      const res: any = editing ? await updateWorkerCategory(editing.id, body) : await createWorkerCategory(body);
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not save", type: "danger" });
        return;
      }
      setOpen(false);
      await reload();
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: WorkerCategory) => {
    const res: any = await updateWorkerCategory(c.id, { active: !c.active });
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Could not update", type: "danger" });
      return;
    }
    await reload();
  };

  const savePolicy = async () => {
    const res: any = await updateWorkerCodePolicy({ format: format.trim(), numbering });
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Could not save", type: "danger" });
      return;
    }
    notifications({ title: "Saved", message: "Code settings saved", type: "success" });
    await reload();
  };

  if (!canManageKarkhana(role)) return null;

  const firstPrefix = categories.find((c) => c.active)?.prefix || "K";
  const formatPreview = previewCode(
    policy ? { ...policy, format: format || policy.format } : null,
    firstPrefix
  );
  const policyDirty = policy && (format.trim() !== policy.format || numbering !== policy.numbering);

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Workers" back={kp.workers()}>
        {canWrite && categories.length ? (
          <HeaderButton primary add onClick={openCreate}>
            Add category
          </HeaderButton>
        ) : null}
      </PageHeader>
      <WorkersNav reviewCount={reviewCount} />

      {loaded && categories.length === 0 ? (
        <>
          <p className="kw-lede">
            Pick a starting set of categories. Each one gives workers a code prefix. You can rename, add or turn off
            categories later; codes already given out never change.
          </p>
          <div className="kw-templates">
            {TEMPLATES.map((t) => (
              <button
                key={t.key}
                type="button"
                className="kw-template"
                disabled={!canWrite || !!seeding}
                onClick={() => seed(t.key)}
              >
                <strong>{seeding === t.key ? "Adding…" : t.title}</strong>
                <span className="kw-muted">{t.body}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="kw-lede">
            The prefix starts every worker code made for that category. Name hints help the import guess a category,
            e.g. "KARIGAR" in a name suggests K.
          </p>
          <p className="kw-lede">
            {t("Workers of a category with an HKP post also show in Users with that post. They can't sign in.")}
          </p>
          <GeoResponsiveList
            rows={categories}
            rowKey={(c) => c.id}
            empty={loaded ? "No categories" : "Loading…"}
            actions={(c) =>
              canWrite ? (
                <StudioRowActions
                  actions={[
                    { key: "edit", label: "Edit", icon: "edit", tone: "primary", onClick: () => openEdit(c) },
                    { key: "active", label: c.active ? "Turn off" : "Turn on", icon: "edit", onClick: () => toggleActive(c) },
                  ]}
                />
              ) : null
            }
            columns={[
              { key: "prefix", header: "Prefix", cell: (c) => <span className="kw-code">{c.prefix}</span> },
              { key: "name", header: "Name", cell: (c) => c.name },
              { key: "pay", header: "Usually paid", hideSm: true, cell: (c) => PAY_LABEL[c.defaultPayBasis] || "—" },
              {
                key: "role",
                header: "On the loom",
                hideSm: true,
                cell: (c) => (c.canWeave ? "Weaves" : c.canHelp ? "Helps" : "—"),
              },
              {
                key: "hints",
                header: "Name hints",
                hideSm: true,
                cell: (c) => ((c.nameHints || []).length ? c.nameHints.join(", ") : "—"),
              },
              {
                key: "post",
                header: t("HKP post"),
                cell: (c) => (c.userRole ? postLabel(c.userRole) : <span className="kw-muted">{t("Karkhana only")}</span>),
              },
              { key: "active", header: "Active", cell: (c) => (c.active ? "On" : "Off") },
            ]}
          />

          <div className="kw-card" style={{ marginTop: 16 }}>
            <h3>New worker codes</h3>
            <p className="kw-lede" style={{ marginBottom: 12 }}>
              When you leave the code blank, HKP makes the next one. Numbers only go up, so a code is never given twice.
            </p>
            <div className="kw-toolbar">
              <div>
                <Label for="kw-format" className="kw-muted">
                  Format
                </Label>
                <Input
                  id="kw-format"
                  className="kw-code"
                  value={format}
                  disabled={!canWrite}
                  onChange={(e) => setFormat(e.target.value)}
                />
              </div>
              <div>
                <Label for="kw-numbering" className="kw-muted">
                  Numbering
                </Label>
                <Input
                  id="kw-numbering"
                  type="select"
                  value={numbering}
                  disabled={!canWrite}
                  onChange={(e) => setNumbering(e.target.value)}
                >
                  <option value="ORG_WIDE">One count for all prefixes (K_1, D_2, J_3…)</option>
                  <option value="PER_PREFIX">Separate count per prefix (K_1, J_1…)</option>
                </Input>
              </div>
            </div>
            <p className="kw-muted" style={{ margin: "4px 0 12px" }}>
              Next code: <span className="kw-code">{formatPreview || "—"}</span>
            </p>
            {canWrite ? (
              <Button size="sm" className="ResetBtn" disabled={!policyDirty} onClick={savePolicy}>
                Save code settings
              </Button>
            ) : null}
          </div>
        </>
      )}

      <Modal isOpen={open} toggle={() => setOpen(false)} modalClassName="kw-modal">
        <ModalHeader toggle={() => setOpen(false)}>{editing ? `Edit ${editing.prefix}` : "Add category"}</ModalHeader>
        <ModalBody>
          <Label for="kc-prefix">Prefix</Label>
          <Input
            id="kc-prefix"
            className="kw-code"
            value={form.prefix}
            maxLength={10}
            onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") })}
            placeholder="K"
            autoFocus={!editing}
          />
          {editing && form.prefix !== editing.prefix ? (
            <p className="kw-muted mt-1">Codes already given out keep {editing.prefix}. Only new codes use the new prefix.</p>
          ) : null}
          <Label className="mt-2" for="kc-name">
            Name
          </Label>
          <Input id="kc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Karigar (weaver)" />
          <Label className="mt-2" for="kc-pay">
            Usually paid
          </Label>
          <Input
            id="kc-pay"
            type="select"
            value={form.defaultPayBasis}
            onChange={(e) => setForm({ ...form, defaultPayBasis: e.target.value })}
          >
            <option value="PIECE">Per piece (e.g. per inch woven)</option>
            <option value="TIME">By time (hours worked)</option>
            <option value="JOB">Per job</option>
          </Input>
          <Label className="mt-2" for="kc-loom">
            On the loom
          </Label>
          <Input
            id="kc-loom"
            type="select"
            value={form.canWeave ? "WEAVE" : form.canHelp ? "HELP" : "NONE"}
            onChange={(e) =>
              setForm({ ...form, canWeave: e.target.value === "WEAVE", canHelp: e.target.value === "HELP" })
            }
          >
            <option value="NONE">Not on the loom</option>
            <option value="WEAVE">Weaves</option>
            <option value="HELP">Helps the weaver</option>
          </Input>
          <Label className="mt-2" for="kc-hints">
            Name hints (comma separated)
          </Label>
          <Input
            id="kc-hints"
            value={form.nameHints}
            onChange={(e) => setForm({ ...form, nameHints: e.target.value })}
            placeholder="KARIGAR"
          />
          <Label className="mt-2" for="kc-post">
            {t("HKP post")}
          </Label>
          <Input
            id="kc-post"
            type="select"
            value={form.userRole}
            onChange={(e) => setForm({ ...form, userRole: e.target.value })}
          >
            <option value="">{t("Karkhana only (no user)")}</option>
            {form.userRole && !posts.some((p) => p.role === form.userRole) ? (
              <option value={form.userRole}>{postLabel(form.userRole)}</option>
            ) : null}
            {posts.map((p) => (
              <option key={p.role} value={p.role}>
                {postLabel(p.role)}
              </option>
            ))}
          </Input>
          <p className="kw-muted mt-1">
            {form.userRole
              ? t("Each worker is added to Users as {{post}}, without a login.", { post: postLabel(form.userRole) })
              : t("Workers stay in Karkhana only.")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={saving} onClick={persist}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaWorkerCategoriesPage;
